import { useSignIn, useSignUp } from '@clerk/clerk-react';
import {
  Button,
  Card,
  CardContent,
  FormFieldGroup,
  Input,
} from '@startupquickstart/frontend';
import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';

/**
 * Landing route for hunts organization invitations.
 *
 * Invitation emails link to Clerk's hosted join URL; Clerk marks the
 * invitation accepted and redirects here with `__clerk_ticket` (the ticket
 * token) and `__clerk_status` appended (per Clerk's accept-organization-
 * invitations custom flow — https://clerk.com/docs/guides/development/
 * custom-flows/organizations/accept-organization-invitations):
 * - `sign_up`   — no account yet: complete sign-up with the ticket.
 * - `sign_in`   — account exists: sign in with the ticket.
 * - `complete`  — already signed in: nothing left to do.
 *
 * Keeping this flow inside the hunts app is what makes hunts invitees land in
 * the hunts admin instead of the tours experience.
 */

const HOME_PATH = '/home';

type FlowState =
  | { phase: 'working' }
  | { phase: 'needs_details' }
  | { phase: 'error'; message: string };

export function AcceptInvitationPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { signIn, isLoaded: signInLoaded } = useSignIn();
  const { signUp, isLoaded: signUpLoaded, setActive } = useSignUp();

  const ticket = searchParams.get('__clerk_ticket');
  const status = searchParams.get('__clerk_status');

  const [flow, setFlow] = useState<FlowState>({ phase: 'working' });
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // React StrictMode double-invokes effects; ticket exchange must run once.
  const startedRef = useRef(false);

  useEffect(() => {
    if (!ticket || !signInLoaded || !signUpLoaded || startedRef.current) return;
    startedRef.current = true;

    const run = async () => {
      try {
        if (status === 'sign_in') {
          const result = await signIn.create({ strategy: 'ticket', ticket });
          if (result.status === 'complete') {
            await setActive({ session: result.createdSessionId });
            navigate(HOME_PATH, { replace: true });
            return;
          }
          setFlow({ phase: 'error', message: 'Could not finish signing you in.' });
          return;
        }

        // sign_up (or unknown): try to complete with just the ticket; fall
        // back to collecting the details the instance requires.
        const result = await signUp.create({ strategy: 'ticket', ticket });
        if (result.status === 'complete') {
          await setActive({ session: result.createdSessionId });
          navigate(HOME_PATH, { replace: true });
          return;
        }
        setFlow({ phase: 'needs_details' });
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'This invitation link is invalid or expired.';
        setFlow({ phase: 'error', message });
      }
    };

    void run();
  }, [ticket, status, signInLoaded, signUpLoaded, signIn, signUp, setActive, navigate]);

  // Already handled (user clicked while signed in), or no ticket at all.
  if (!ticket) {
    return <Navigate to={HOME_PATH} replace />;
  }
  if (status === 'complete') {
    return <Navigate to={HOME_PATH} replace />;
  }

  const handleCompleteSignUp = async () => {
    if (!signUp) return;
    setSubmitting(true);
    try {
      const result = await signUp.update({ firstName, lastName, password });
      if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId });
        navigate(HOME_PATH, { replace: true });
        return;
      }
      setFlow({ phase: 'error', message: 'Sign-up could not be completed. Please try again.' });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Sign-up failed. Please try again.';
      setFlow({ phase: 'error', message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-muted/30 px-4">
      <Card className="w-full max-w-md">
        <CardContent className="space-y-6 p-8">
          <div className="space-y-1 text-center">
            <p className="text-xs font-semibold uppercase tracking-widest text-primary">
              Destination Hunts
            </p>
            <h1 className="text-2xl font-semibold tracking-tight">Join your team</h1>
          </div>

          {flow.phase === 'working' && (
            <p className="text-center text-sm text-muted-foreground" role="status">
              Accepting your invitation…
            </p>
          )}

          {flow.phase === 'error' && (
            <div className="space-y-4">
              <p className="text-center text-sm text-destructive" role="alert">
                {flow.message}
              </p>
              <p className="text-center text-sm text-muted-foreground">
                Ask your admin to send a new invitation, or sign in if you already have an
                account.
              </p>
              <div className="flex justify-center">
                <Button variant="outline" onClick={() => navigate(HOME_PATH)}>
                  Go to Destination Hunts
                </Button>
              </div>
            </div>
          )}

          {flow.phase === 'needs_details' && (
            <form
              className="space-y-4"
              onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
                e.preventDefault();
                void handleCompleteSignUp();
              }}
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FormFieldGroup name="firstName" label="First name" required>
                  <Input
                    id="firstName"
                    autoComplete="given-name"
                    placeholder="First name"
                    value={firstName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFirstName(e.target.value)
                    }
                  />
                </FormFieldGroup>
                <FormFieldGroup name="lastName" label="Last name" required>
                  <Input
                    id="lastName"
                    autoComplete="family-name"
                    placeholder="Last name"
                    value={lastName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setLastName(e.target.value)
                    }
                  />
                </FormFieldGroup>
              </div>
              <FormFieldGroup name="password" label="Password" required>
                <Input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  placeholder="Create a password"
                  value={password}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setPassword(e.target.value)
                  }
                />
              </FormFieldGroup>
              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
                <Button type="submit" loading={submitting}>
                  Create account &amp; join
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </main>
  );
}
